import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import DashboardLayout from '../components/DashboardLayout';
import { Sparkles, Trash2, Loader2, AlertTriangle, RefreshCw, Users, Truck, ClipboardList, FileText, Megaphone } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Super-admin console for listing every ``is_demo:true`` tenant with
 * per-row purge and a master "Purge All" action. Useful between sales
 * demos to keep the live dashboard clean of seeded data.
 */
const DemoTenants = () => {
  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [purgingId, setPurgingId] = useState(null);
  const [purgingAll, setPurgingAll] = useState(false);
  const [confirmPurgeAll, setConfirmPurgeAll] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/superadmin/demo-tenants`, { withCredentials: true });
      setTenants(r.data.tenants || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load demo tenants');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const purgeOne = async (t) => {
    if (!window.confirm(`Purge "${t.name}"? This deletes ${Object.values(t.counts).reduce((s, n) => s + n, 0)}+ records and cannot be undone.`)) return;
    setPurgingId(t.id);
    try {
      const r = await axios.post(`${API}/superadmin/demo-tenants/${t.id}/purge`, {}, { withCredentials: true });
      toast.success(`Purged "${t.name}" — ${r.data.total_deleted} records deleted`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Purge failed');
    } finally {
      setPurgingId(null);
    }
  };

  const purgeAll = async () => {
    setPurgingAll(true);
    try {
      const r = await axios.post(`${API}/superadmin/demo-tenants/purge-all`, {}, { withCredentials: true });
      toast.success(`Purged ${r.data.tenants_purged} tenants — ${r.data.total_deleted} records deleted`);
      setConfirmPurgeAll(false);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Purge-all failed');
    } finally {
      setPurgingAll(false);
    }
  };

  return (
    <DashboardLayout>
      <div className="p-6 md:p-8" data-testid="demo-tenants-page">
        <header className="mb-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <div className="flex items-center gap-3 mb-1">
              <Sparkles className="w-7 h-7 text-[#002FA7]" />
              <h1 className="text-3xl md:text-4xl font-bold tracking-tight text-slate-900" style={{ fontFamily: 'Work Sans, sans-serif' }}>
                Demo Tenants
              </h1>
            </div>
            <p className="text-slate-600 text-sm">Every company flagged <code className="font-mono text-xs bg-slate-100 px-1 py-0.5 rounded">is_demo:true</code>. Purge them between sales campaigns.</p>
          </div>
          <div className="flex items-center gap-2">
            <button
              data-testid="demo-tenants-refresh"
              onClick={load}
              className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-slate-300 bg-white text-slate-700 text-sm font-semibold hover:bg-slate-50"
            >
              <RefreshCw className="w-4 h-4" /> Refresh
            </button>
            <button
              data-testid="demo-tenants-purge-all-open"
              onClick={() => setConfirmPurgeAll(true)}
              disabled={tenants.length === 0}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-red-600 hover:bg-red-700 text-white text-sm font-semibold disabled:bg-slate-300 disabled:cursor-not-allowed"
            >
              <Trash2 className="w-4 h-4" /> Purge all demos
            </button>
          </div>
        </header>

        <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
          {loading ? (
            <div className="p-10 text-center text-slate-500" data-testid="demo-tenants-loading">
              <Loader2 className="w-4 h-4 animate-spin inline mr-2" /> Loading…
            </div>
          ) : tenants.length === 0 ? (
            <div className="p-10 text-center text-slate-500" data-testid="demo-tenants-empty">
              No demo tenants right now. Go to <a href="/admin/seed-tenant" className="text-[#002FA7] hover:underline">Seed Demo Tenant</a> to create one.
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-600">
                <tr>
                  <th className="px-4 py-3 text-left">Company</th>
                  <th className="px-4 py-3 text-left">Created</th>
                  <th className="px-4 py-3 text-left">Contents</th>
                  <th className="px-4 py-3 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {tenants.map((t) => (
                  <tr key={t.id} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`demo-tenant-row-${t.id}`}>
                    <td className="px-4 py-3">
                      <p className="font-semibold text-slate-900">{t.name}</p>
                      <p className="text-xs text-slate-500 font-mono">{t.email || t.id}</p>
                    </td>
                    <td className="px-4 py-3 text-slate-600">
                      {t.created_at ? new Date(t.created_at).toLocaleString() : '—'}
                    </td>
                    <td className="px-4 py-3 text-xs text-slate-700">
                      <div className="flex flex-wrap gap-x-3 gap-y-1">
                        <span className="flex items-center gap-1"><Users className="w-3 h-3" /> {t.counts.users}</span>
                        <span className="flex items-center gap-1"><Truck className="w-3 h-3" /> {t.counts.drivers}</span>
                        <span className="flex items-center gap-1"><ClipboardList className="w-3 h-3" /> {t.counts.loads}</span>
                        <span className="flex items-center gap-1"><FileText className="w-3 h-3" /> {t.counts.invoices}</span>
                        <span className="flex items-center gap-1"><Megaphone className="w-3 h-3" /> {t.counts.announcements}</span>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <button
                        data-testid={`demo-tenant-purge-${t.id}`}
                        onClick={() => purgeOne(t)}
                        disabled={purgingId === t.id}
                        className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md text-red-600 border border-red-300 bg-white hover:bg-red-50 text-xs font-semibold disabled:opacity-50"
                      >
                        {purgingId === t.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <Trash2 className="w-3 h-3" />}
                        Purge
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {confirmPurgeAll && (
          <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" data-testid="purge-all-modal">
            <div className="bg-white rounded-xl shadow-xl w-full max-w-md p-6">
              <div className="flex items-center gap-3 mb-4">
                <AlertTriangle className="w-6 h-6 text-red-600" />
                <h3 className="text-lg font-bold text-slate-900">Purge all demo tenants?</h3>
              </div>
              <p className="text-sm text-slate-700 mb-4">
                This will permanently delete <strong>{tenants.length}</strong> demo compan{tenants.length === 1 ? 'y' : 'ies'} and every record they contain (users, drivers, loads, invoices, announcements). This cannot be undone.
              </p>
              <div className="flex justify-end gap-2">
                <button
                  onClick={() => setConfirmPurgeAll(false)}
                  disabled={purgingAll}
                  className="px-4 py-2 rounded-lg border border-slate-300 bg-white hover:bg-slate-50 text-sm font-semibold"
                >
                  Cancel
                </button>
                <button
                  data-testid="demo-tenants-purge-all-confirm"
                  onClick={purgeAll}
                  disabled={purgingAll}
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-red-600 hover:bg-red-700 text-white text-sm font-semibold disabled:opacity-50"
                >
                  {purgingAll && <Loader2 className="w-4 h-4 animate-spin" />}
                  Purge all
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
};

export default DemoTenants;
